import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { api, errMsg, fmtMoney, fmtValue } from "@/lib/api";

const FILTERS = [["", "Tutti"], ["bozza", "Bozza"], ["in_firma", "In firma"], ["firmato", "Firmati"], ["annullato", "Annullati"]];

export default function Dashboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/contracts").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const counts = useMemo(() => (rows || []).reduce((m, r) => ({ ...m, [r.status]: (m[r.status] || 0) + 1 }), {}), [rows]);
  const list = useMemo(() => (rows || []).filter((r) => (!filter || r.status === filter) &&
    (!q || `${r.number} ${r.data?.cliente_nome || ""} ${r.data?.evento_data || ""} ${fmtValue({ type: "date" }, r.data?.evento_data)}`.toLowerCase().includes(q.toLowerCase()))), [rows, filter, q]);

  const create = async () => {
    try {
      const r = await api.post("/contracts");
      nav(`/contratti/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#7B929C]">Registro contratti</div>
          <h1 className="font-serif-it text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-2">Contratti di evento</h1>
        </div>
        <Button data-testid="new-contract-button" onClick={create} className="rounded-full h-11 px-6"><Plus size={16} className="mr-2" /> Nuovo contratto</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["bozza", "Bozze"], ["in_firma", "In attesa di firma"], ["firmato", "Firmati"], ["annullato", "Annullati"]].map(([k, l]) => (
          <div key={k} className="rounded-xl border border-[#D4E6EC] bg-white p-4" data-testid={`stat-${k}`}>
            <div className="text-xs text-[#7B929C]">{l}</div>
            <div className="font-serif-it text-3xl font-semibold">{counts[k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([k, l]) => (
            <button key={k || "all"} data-testid={`filter-${k || "all"}`} onClick={() => setFilter(k)}
              className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-[#0D2B3A] text-white border-[#0D2B3A]" : "bg-white border-[#D4E6EC] hover:border-[#0B6E8F]"}`}>{l}</button>
          ))}
        </div>
        <div className="relative md:w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B929C]" />
          <input data-testid="contracts-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, numero o data…"
            className="w-full rounded-full border border-[#D4E6EC] bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B6E8F]/30" />
        </div>
      </div>

      <div className="rounded-xl border border-[#D4E6EC] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="contracts-table">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#7B929C] border-b border-[#D4E6EC]">
            <tr><th className="p-4">N.</th><th className="p-4">Cliente</th><th className="p-4">Evento</th><th className="p-4">Sede</th><th className="p-4 text-right">Prezzo rif.</th><th className="p-4">Stato</th></tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="p-6 text-[#7B929C]">Caricamento…</td></tr>}
            {rows && list.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#7B929C]" data-testid="contracts-empty">Nessun contratto. Crea il primo con «Nuovo contratto».</td></tr>}
            {list.map((r) => (
              <tr key={r.id} data-testid={`contract-row-${r.number}`} onClick={() => nav(`/contratti/${r.id}`)} className="border-b border-[#EAF5F7] hover:bg-[#F5FAFB] cursor-pointer transition-colors">
                <td className="p-4 font-mono-it text-xs">{r.number}</td>
                <td className="p-4 font-medium">{r.data?.cliente_nome || <span className="text-[#A8C6CF]">—</span>}</td>
                <td className="p-4">{r.data?.evento_tipo || ""} <span className="text-[#7B929C]">{fmtValue({ type: "date" }, r.data?.evento_data)}</span></td>
                <td className="p-4 text-xs">{r.data?.sede || ""}</td>
                <td className="p-4 text-right">{r.data?.prezzo_riferimento ? fmtMoney(r.data.prezzo_riferimento) : ""}</td>
                <td className="p-4"><StatusBadge status={r.status} testId={`contract-status-${r.number}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
